import { IPromise, IQService } from "angular";
import { IModalService } from "angular-ui-bootstrap";
import { IStateInit } from "@gtmhub/core/routing";
import { IIndicator, IUIError } from "@gtmhub/error-handling";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { IBadgeAssignment, IBadgesIdMap } from "../models/badges";
import { BadgesActions } from "../redux/badges-actions";
import { IBadgesStoreState } from "../redux/badges-reducer";
import { AnnouncementService } from "../services/announcements.service";
import { BadgeService } from "../services/badges";

interface IApproveBadgesScope extends IExtendedRootScopeService, IScopeFromRedux {
  indicators: { gettingBadges?: IIndicator };
  badgeAssignments: IBadgeAssignment[];
  approveBadgeAward(badgeAssignment: IBadgeAssignment): void;
  rejectBadgeAward(badgeAssignment: IBadgeAssignment): void;
}

interface IScopeFromRedux {
  badges: IBadgesIdMap;
  badgesFetched: boolean;
  badgesError: IUIError;
}

const bindStateToScope = (state: IBadgesStoreState): IScopeFromRedux => ({
  badges: state.badges.map,
  badgesFetched: state.badges.isFetched,
  badgesError: state.badges.error,
});

export class ApproveBadgesCtrl implements IStateInit {
  public static $inject = ["$scope", "$uibModal", "$q", "BadgeService", "AnnouncementService", "BadgesActions", "$ngRedux"];

  constructor(
    private $scope: IApproveBadgesScope,
    private $uibModal: IModalService,
    private $q: IQService,
    private badgeService: BadgeService,
    private announcementService: AnnouncementService,
    private badgesActions: BadgesActions,
    private $ngRedux: INgRedux
  ) {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.$scope.approveBadgeAward = this.approveBadgeAward;
    this.$scope.rejectBadgeAward = this.rejectBadgeAward;
    this.$scope.indicators = {};
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.$ngRedux.dispatch(this.badgesActions.fetchBadgesIfMissing());

    return this.getBadgesToBeApproved();
  };

  private getBadgesToBeApproved = (): IPromise<unknown> => {
    this.$scope.indicators.gettingBadges = { progress: true };

    return this.$q
      .all([
        this.badgeService.getBadgesToBeApproved(),
        this.$scope.watchUntilAndRejectOnError(
          () => this.$scope.badgesFetched,
          () => this.$scope.badgesError
        ),
      ])
      .then(
        ([badgeAssignments]) => {
          delete this.$scope.indicators.gettingBadges;
          this.$scope.badgeAssignments = badgeAssignments.items;
        },
        (error) => {
          this.$scope.indicators.gettingBadges = { error };
        }
      );
  };

  private approveBadgeAward = (badgeAssignment: IBadgeAssignment) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      template: require("../views/confirm-approve.html"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        "$uibModalInstance",
        function ($scope, $uibModalInstance) {
          $scope.approve = () => {
            $scope.indicators = { approving: { progress: true } };
            ctrl.badgeService.approveBadgeAward(badgeAssignment.id).then(
              () => {
                delete $scope.indicators.approving;
                const announcement = ctrl.badgeService.configureRewardAnnouncement(badgeAssignment);
                ctrl.announcementService.post(announcement);
                ctrl.getBadgesToBeApproved();
                $uibModalInstance.close();
              },
              (err) => ($scope.indicators.approving = { error: err })
            );
          };

          $scope.close = () => {
            $uibModalInstance.close();
          };
        },
      ],
    });
  };

  private rejectBadgeAward = (badgeAssignment: IBadgeAssignment) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      template: require("../views/confirm-reject.html"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        "$uibModalInstance",
        function ($scope, $uibModalInstance) {
          $scope.reject = () => {
            $scope.indicators = { rejecting: { progress: true } };
            ctrl.badgeService.rejectBadgeAward(badgeAssignment.id).then(
              () => {
                delete $scope.indicators.rejecting;
                ctrl.getBadgesToBeApproved();
                $uibModalInstance.close();
              },
              (err) => ($scope.indicators.rejecting = { error: err })
            );
          };

          $scope.close = () => {
            $uibModalInstance.close();
          };
        },
      ],
    });
  };
}
